import { useMemo } from "react";
import { PrivilegesModel } from "../api/models/PrivilegesModel";

type PrivilegeKey = keyof PrivilegesModel;

export type MainRole = PrivilegeKey | "unknown";

export const ALL_ROLES: PrivilegeKey[] = ["master", "admin", "user", "locked"];

export const getUserRoles = (privs: PrivilegesModel | null | undefined): MainRole[] => {
  if (!privs) return [];
  // only check known keys, fully typed
  const roles = ALL_ROLES.filter((role) => !!privs?.[role]);
  return roles;
};

export const getUserMainRole = (privs: PrivilegesModel | null | undefined): MainRole => {
  const order: PrivilegeKey[] = ["master", "admin", "user", "locked"];
  if (!privs) return "unknown";
  const main = order.find((role) => !!privs[role]);
  return main ?? "unknown";
};

export const hasUserRole = (
  privs: PrivilegesModel | null | undefined,
  role: PrivilegeKey,
): boolean => !!privs?.[role];

interface UseUserRolesReturn {
  privileges: PrivilegesModel | null;

  hasRole: (role: PrivilegeKey) => boolean;
  hasAnyRole: (roles: PrivilegeKey[]) => boolean;
  hasAllRoles: (roles: PrivilegeKey[]) => boolean;

  allRoles: MainRole[];
  mainRole: MainRole;
  isLocked: boolean;
  isUnknown: boolean;

  isMasterLevel: boolean;
  isAdminLevel: boolean;
  isUserLevel: boolean;
  isCompanyAdmin: boolean;

  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
  canManageCompanies: boolean;
  canManageProjects: boolean;
  canVerifySignatures: boolean;
}

export const useUserRoles = (
  privileges: PrivilegesModel | null | undefined,
): UseUserRolesReturn => {
  const roleInfo = useMemo(() => {
    return {
      allRoles: getUserRoles(privileges),
      mainRole: getUserMainRole(privileges),
      hasRole: (role: PrivilegeKey) => hasUserRole(privileges, role),
    };
  }, [privileges]);

  const hasRole = roleInfo.hasRole;

  const hasAnyRole = useMemo(
    () => (roles: PrivilegeKey[]) => roles.some((role) => hasRole(role)),
    [hasRole],
  );

  const hasAllRoles = useMemo(
    () => (roles: PrivilegeKey[]) => roles.every((role) => hasRole(role)),
    [hasRole],
  );

  const isLocked = hasRole("locked");
  const isUnknown = roleInfo.mainRole === "unknown";

  const isMasterLevel = hasRole("master");
  const isAdminLevel = hasAnyRole(["master", "admin"]);
  const isUserLevel = hasAnyRole(["master", "admin", "user"]);

  /**
   * A COMPANY's own admin — admin, and explicitly NOT master (7HAI-981).
   *
   * Jari: "master is ONLY user who control service - it does not calculate etc
   * anything -> control companies & users that is it."
   *
   * `isAdminLevel` above is master-OR-admin, which is right for most admin
   * screens: master administers companies and users, so it needs the surfaces
   * that do that. It is WRONG for a screen that configures how a company
   * estimates and prices work, because master is not a company and never quotes
   * from one — its own companyID is the retailer account, so anything it saved
   * there could not reach a bid. That is a category error, not a shortcut.
   *
   * Use this for a company's commercial/estimating settings; keep
   * `isAdminLevel` for platform administration.
   */
  const isCompanyAdmin = hasRole("admin") && !hasRole("master");

  const canCreate = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isUserLevel;
  }, [isLocked, isUnknown, isUserLevel]);

  const canRead = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isUserLevel;
  }, [isLocked, isUnknown, isUserLevel]);

  const canUpdate = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isUserLevel;
  }, [isLocked, isUnknown, isUserLevel]);

  const canDelete = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isAdminLevel;
  }, [isLocked, isUnknown, isAdminLevel]);

  const canManageUsers = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isAdminLevel;
  }, [isLocked, isUnknown, isAdminLevel]);

  const canManageCompanies = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isMasterLevel;
  }, [isLocked, isUnknown, isMasterLevel]);

  const canManageProjects = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isAdminLevel;
  }, [isLocked, isUnknown, isAdminLevel]);

  const canVerifySignatures = useMemo(() => {
    if (isLocked || isUnknown) return false;
    return isUserLevel;
  }, [isLocked, isUnknown, isUserLevel]);

  return {
    privileges: privileges ?? null,

    hasRole,
    hasAnyRole,
    hasAllRoles,

    allRoles: roleInfo.allRoles,
    mainRole: roleInfo.mainRole,
    isLocked,
    isUnknown,

    isMasterLevel,
    isAdminLevel,
    isUserLevel,
    isCompanyAdmin,

    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canManageUsers,
    canManageCompanies,
    canManageProjects,
    canVerifySignatures,
  };
};
